import
{
    CHAT_ACTION_NAME,
    ChatActionBase,
    IChatActionList,
    IChatActionsData, IChatInputActionItem
} from "@core/base/chat-action-base";
import { ICoreDependencies } from "@core/core-boot/core-boot.types";
import { OfficeChat } from "@core/office/office-chat/office-chat";
import { OfficeGroupChat } from "@core/office/office-group-chat/office-group-chat";
import { OfficeService } from "@core/office/office-service/office-service";
import { OfficeSessionService } from "@core/office/office-session/office.session.service";
import { OutlookHost } from "@core/shared/office-hosts/outlook-host";
import { ChatUtilsService } from "@core/shared/chat-utils.service";
import type { IGroupChatUiMessage } from "@core/types/group-chat-message.types";
import { environment } from "@env";
import { logger } from "@services/logger/logger.service";
import { distinctUntilChanged, filter, map, skip } from "rxjs";
import { checkAndCutByBytes } from "@utils/string";

export class OfficeChatAction implements ChatActionBase
{
    private static readonly SUPPORTED_IMAGE_EXTENSIONS = new Set([
        'png',
        'gif',
        'bmp',
        'dib',
        'jpg',
        'jpe',
        'jfif',
        'jpeg',
    ]);

    private clipboard = this.dependencies.clipboard;
    private documentContextGeneration = 0;
    private directChoiceGeneration = 0;
    private documentContextKey: string | null | undefined;
    private pendingDirectChoice: Promise<void> = Promise.resolve();
    private contextReset?: () => void;
    private historyLoadGeneration = 0;
    private chatEntryAccount: string | null = null;

    public get documentPreferenceVersion(): string
    {
        return [ this.documentContextGeneration, this.directChoiceGeneration,
            this.dependencies.userSettingsApi?.generation ?? 0,
            this.sessionService?.getSession()?.user_id ?? '' ].join(':');
    }

    public get historyPreferenceVersion(): string
    {
        return [ this.historyLoadGeneration, this.dependencies.userSettingsApi?.generation ?? 0,
            this.sessionService?.getSession()?.user_id ?? '' ].join(':');
    }

    public action: IChatActionList = {
        [ CHAT_ACTION_NAME.selection ]: {
            value: false,
            onBuildData: this.buildDataForSelection
        },
        [ CHAT_ACTION_NAME.attachDocument ]: {
            value: false,
            // Collected by OfficeChatV1 once the executing skill's override is known.
            onBuildData: () => undefined
        },
        [ CHAT_ACTION_NAME.documentChat ]: {
            value: false,
            onBuildData: this.buildDataForDocumentChat
        },
        [ CHAT_ACTION_NAME.clipboard ]: {
            value: false,
            onBuildData: this.buildDataForClipboard
        },
        [ CHAT_ACTION_NAME.history ]: {
            value: true,
            onBuildData: this.buildDataForHistory
        },
        [ CHAT_ACTION_NAME.img ]: {
            value: false,
            onBuildData: this.buildDataForImg
        }
    };
    private actionData: IChatActionsData = {
        attachDocument: '',
        documentChat: '',
        img: false,
        history: [],
        selection: '',
        clipboard: ''
    };

    constructor(
        private dependencies: ICoreDependencies,
        private officeService: OfficeService,
        private chatService: OfficeChat,
        private chatUtils: ChatUtilsService,
        private groupChatService: OfficeGroupChat,
        private sessionService?: OfficeSessionService,
    ) { }

    public onDocumentContextReset(handler: () => void): void
    {
        this.contextReset = handler;
    }

    public removeDocumentContextReset(handler: () => void): void
    {
        if (this.contextReset === handler)
        {
            this.contextReset = undefined;
        }
    }

    public observeDocumentContext(): void
    {
        this.dependencies.userSettingsApi?.settings$?.subscribe(settings =>
        {
            if (!settings) return;
            this.action.history.value = settings.messageHistoryId !== null;
            if (!this.action.selection.value && !this.action.clipboard.value && !this.action.img.value)
            {
                this.action.attachDocument.value = !!settings.documentContextEnabled;
                this.action.documentChat.value = !!settings.documentChatEnabled;
            }
        });
        this.chatService?.currentThreadId$?.()?.pipe(skip(1), distinctUntilChanged()).subscribe(() =>
        {
            const generation = ++this.historyLoadGeneration;
            void this.dependencies.userSettingsApi.getHistoryEnabled(true).then(enabled =>
            {
                if (generation === this.historyLoadGeneration && enabled !== null)
                    this.action.history.value = enabled;
            });
        });
        this.sessionService?.sessionInfo$.pipe(
            map(session => session.user_id),
            distinctUntilChanged(),
            filter(userId => !!userId),
        ).subscribe(() => void this.restoreDocumentContext(true));
        void this.officeService.getHost().onDocumentChange(async () =>
        {
            await this.restoreDocumentContext(true);
        });
    }

    public async restoreDocumentContext(reset = false, chatEntry = false): Promise<boolean>
    {
        const account = this.sessionService?.getSession()?.user_id ?? null;
        const refreshSettings = chatEntry && !!account && this.chatEntryAccount === account;
        if (chatEntry) this.chatEntryAccount = account;
        const generation = ++this.documentContextGeneration;
        if (reset)
        {
            this.contextReset?.();
            for (const name of [ CHAT_ACTION_NAME.selection, CHAT_ACTION_NAME.clipboard, CHAT_ACTION_NAME.img, CHAT_ACTION_NAME.documentChat ])
            {
                this.action[ name ].value = false;
            }
            this.action.attachDocument.value = false;
        }

        const [ key, settings ] = await Promise.all([
            this.resolveDocumentContextKey(),
            this.dependencies.userSettingsApi?.getSettings(refreshSettings),
        ]);
        if (generation !== this.documentContextGeneration)
        {
            return false;
        }

        if (reset || key !== this.documentContextKey)
        {
            if (!reset && this.documentContextKey !== undefined)
            {
                this.contextReset?.();
                for (const name of [ CHAT_ACTION_NAME.selection, CHAT_ACTION_NAME.clipboard, CHAT_ACTION_NAME.img ])
                {
                    this.action[ name ].value = false;
                }
            }
            this.documentContextKey = key;
        }
        const temporarySourceActive = this.action.selection.value || this.action.clipboard.value || this.action.img.value;
        this.action.attachDocument.value = !!settings?.documentContextEnabled && !temporarySourceActive;
        this.action.documentChat.value = !!settings?.documentChatEnabled && !temporarySourceActive;
        return true;
    }

    public cancelPendingDocumentContext(): void
    {
        this.documentContextGeneration++;
    }

    public setDocumentContextFromClick(enabled: boolean): Promise<boolean>
    {
        return this.saveDocumentChoice({ documentContextEnabled: enabled,
            ...(enabled ? { documentChatEnabled: false } : {}) });
    }

    public setDocumentChatFromClick(enabled: boolean): Promise<boolean>
    {
        return this.saveDocumentChoice({ documentChatEnabled: enabled,
            ...(enabled ? { documentContextEnabled: false } : {}) });
    }

    private saveDocumentChoice(patch: { documentContextEnabled?: boolean; documentChatEnabled?: boolean; }): Promise<boolean>
    {
        const choice = ++this.directChoiceGeneration;
        const contextGeneration = this.documentContextGeneration;
        const previous = this.pendingDirectChoice;
        const work = (async () =>
        {
            await previous;
            if (choice !== this.directChoiceGeneration) return false;
            const updated = await this.dependencies.userSettingsApi?.patch(patch);
            if (!updated) return false;
            if (contextGeneration === this.documentContextGeneration && choice === this.directChoiceGeneration)
            {
                this.action.attachDocument.value = !!updated.documentContextEnabled;
                this.action.documentChat.value = !!updated.documentChatEnabled;
            }
            return true;
        })();
        this.pendingDirectChoice = work.then(() => undefined);
        return work;
    }

    public async waitForDocumentContext(): Promise<void>
    {
        await this.pendingDirectChoice;
        while (!await this.restoreDocumentContext())
        {
            // A newer account or item change superseded the pending resolution.
        }
    }

    public noteHistoryChoice(): void { ++this.historyLoadGeneration; }

    private async resolveDocumentContextKey(): Promise<string | null>
    {
        const userId = this.sessionService?.getSession()?.user_id;
        if (!userId?.trim())
        {
            return null;
        }

        const host = this.officeService.getHost();
        const snapshot = host instanceof OutlookHost ? host.captureSelection() : null;
        let documentId: string | null = snapshot?.itemId || snapshot?.internetMessageId || null;
        if (!snapshot)
        {
            try
            {
                documentId = await host.getDocumentIDAsync();
            }
            catch
            {
                return null;
            }
        }

        if (!snapshot)
        {
            try
            {
                if (await host.getDocumentIDAsync() !== documentId)
                {
                    return null;
                }
            }
            catch
            {
                return null;
            }
        }

        if (this.sessionService?.getSession()?.user_id !== userId
            || host !== this.officeService.getHost()
            || (snapshot && host instanceof OutlookHost && !host.isSelectionCurrent(snapshot)))
        {
            return null;
        }

        const identity = documentId?.trim();
        if (!identity || identity.toLowerCase() === 'onenote')
        {
            return null;
        }
        return JSON.stringify([ userId, host.getTag(), identity ]);
    }

    public async setTextToHost(text: string): Promise<void>
    {
        await this.officeService.setSelectedFormattedTextAsync(
            this.clipboard.convertMarkdownToPlainText(text),
            this.clipboard.convertMarkdownToHtml(text),
        );
    }

    public async setImageToHost(imageSource: string): Promise<void>
    {
        try
        {
            await this.insertImageToHost(imageSource);
        }
        catch (error)
        {
            logger.error(error);
            throw error;
        }
    }

    private async insertImageToHost(imageSource: string): Promise<void>
    {
        if (!imageSource)
        {
            throw new TypeError('Image source is not defined');
        }

        const dataUrl = await this.resolveImageDataUrl(imageSource);
        if (!dataUrl)
        {
            throw new TypeError('Cannot resolve image as data URL');
        }

        const base64StartIndex = dataUrl.indexOf(';base64,');
        if (base64StartIndex < 0)
        {
            throw new TypeError('Invalid image data URL');
        }

        const base64 = dataUrl.substring(base64StartIndex + 8);
        const imageMimetype = dataUrl.substring(5, base64StartIndex);
        const imageExtension = this.resolveSupportedImageExtension(imageMimetype);
        const fileName = `image-${ Date.now() }.${ imageExtension }`;

        await this.officeService.setSelectedImageAsync(fileName, base64);
    }

    private async resolveImageDataUrl(imageSource: string): Promise<string | null>
    {
        if (imageSource.startsWith('data:image/'))
        {
            return imageSource;
        }

        if (!/^https?:\/\//i.test(imageSource))
        {
            return null;
        }

        try
        {
            const response = await fetch(imageSource);
            if (!response.ok)
            {
                return null;
            }

            const imageBlob = await response.blob();
            return await this.blobToDataUrl(imageBlob);
        }
        catch (error)
        {
            logger.error(error);
            return null;
        }
    }

    private blobToDataUrl(blob: Blob): Promise<string>
    {
        return new Promise((resolve, reject) =>
        {
            const reader = new FileReader();

            reader.onloadend = () =>
            {
                if (typeof reader.result === 'string')
                {
                    resolve(reader.result);
                    return;
                }

                reject(new TypeError('Failed to convert blob to data URL'));
            };

            reader.onerror = () => reject(new TypeError('Failed to read image blob'));
            reader.readAsDataURL(blob);
        });
    }

    private resolveSupportedImageExtension(imageMimetype: string): string
    {
        const extension = imageMimetype
            .toLowerCase()
            .split('/')[ 1 ]
            ?.split(';')[ 0 ]
            ?.trim();

        if (extension && OfficeChatAction.SUPPORTED_IMAGE_EXTENSIONS.has(extension))
        {
            return extension;
        }

        return 'png';
    }

    public isReadableDocumentHost(): boolean
    {
        const host = this.officeService.getHost();
        return host.checkDocumentReadable();
    }

    public isEditableHost(): boolean
    {
        const host = this.officeService.getHost();
        return host.checkEditable();
    }

    public async getSelectedContentFromHost(): Promise<string>
    {
        const host = this.officeService.getHost();
        return host.getSelectedContentAsync();
    }

    public async getFullDocumentFromHost(): Promise<string>
    {
        const host = this.officeService.getHost();
        return host.getDocumentTextAsync();
    }

    public getActions(): IChatActionList
    {
        return this.action;
    }

    public async getActionsData(): Promise<IChatActionsData>
    {
        for (const [ key, item ] of Object.entries(this.action))
        {
            await item.onBuildData.call(this, item);
        }
        return { ...this.actionData };
    }

    private async buildDataForSelection(action: IChatInputActionItem): Promise<void>
    {
        if (action.value)
        {
            const data = await this.getSelectedContentFromHost();
            this.actionData.selection = await checkAndCutByBytes(data, environment.limits_bytes.ATTACH_SELECTED);
            return;
        }
        this.actionData.selection = '';
    }

    private async buildDataForDocumentChat(action: IChatInputActionItem): Promise<void>
    {
        if (!action.value)
        {
            this.actionData.documentChat = '';
            return;
        }

        const history: IGroupChatUiMessage[] = this.groupChatService
            .getMessagesValue()
            .filter(message => !!message.text.trim());

        if (!history.length)
        {
            this.actionData.documentChat = '';
            return;
        }

        this.actionData.documentChat = await checkAndCutByBytes(
            JSON.stringify(history),
            environment.limits_bytes.ATTACH_DOCUMENT,
        );
    }

    private async buildDataForClipboard(action: IChatInputActionItem): Promise<void>
    {
        if (action.value)
        {
            const data = await this.clipboard.copyFrom();
            this.actionData.clipboard = data || '';
            return;
        }
        this.actionData.clipboard = '';
    }

    private buildDataForHistory(action: IChatInputActionItem): void
    {
        // const messages = this.chatService.getMessagesValue();

        // if (action.value)
        // {
        //     const historyCount = environment.history_count_message;
        //     const lastMessages = findLastElements(messages, historyCount);
        //     this.actionData.history = lastMessages.reduce<typeof this.actionData.history>((result, item) =>
        //     {
        //         const isBotMessage = this.chatUtils.isSenderBot;
        //         const isValueImage = this.chatUtils.isValueImg.bind(this.chatUtils);
        //         if (isBotMessage(item) && isValueImage(item))
        //         {
        //             // TODO ask question about,will image send for history?
        //             return result;
        //         }

        //         const value = item.value || (item as IChatMessageUser).meta?.original_text || '';

        //         result.push({ role: item.sender, content: value });
        //         return result;
        //     }, []);
        //     return;
        // }

        this.actionData.history = [];
    }

    private buildDataForImg(action: IChatInputActionItem): void
    {
        if (action.value)
        {
            this.actionData.img = true;
            return;
        }
        this.actionData.img = false;
    }
}
